import {DEVICE_EVENT, HOLDER_EXTRACTED, HOLDER_INSERTED} from '../../consts/uamf/uamfDeviceEventTypes';
import * as uamfWebSocketEventTypes from '../../consts/uamf/uamfWebSocketEventTypes';
import {UAMF_WS_PONG_RESPONSE} from '../../consts/uamf/uamfWebSocketValues';
import {selectIsDeviceRegisteredByDusn} from '../../state/selectors/iotDevices';
import {setHolderLastChargerDusn, updateDeviceData} from '../../state/slice/iotDevices';
import {dispatch, getState} from '../../state/store';
import log from '../logger/log';
import uamfAsyncRequestService from './uamfAsyncRequestService';
import uamfDeviceService from './uamfDeviceService';
import uamfMappingService from './uamfMappingService';
import UamfWebSocketKeepAliveManager from './uamfWebSocketKeepAliveManager';

const proceedMessage = (message, assetId) => {
    const data = JSON?.parse(message.data) || {};
    const {eventType, response, timestamp} = data || {};

    if (response === UAMF_WS_PONG_RESPONSE) {
        log.debug('uamfWebSocketResponseService: Received pong response');
        UamfWebSocketKeepAliveManager.getInstance(assetId)?.onPongReceived();
        return;
    }

    switch (eventType) {
        case uamfWebSocketEventTypes.CONNECTION_STATUS_CHANGE:
            proceedConnectionChange(data);
            break;
        case uamfWebSocketEventTypes.ASYNC_REQUEST:
            manageAsyncRequestMessage(data);
            break;
        case uamfWebSocketEventTypes.DEVICE_DATA_CHANGE:
            manageDeviceDataChangeMessage(data);
            break;
        case uamfWebSocketEventTypes.HEARTBEAT:
            handleHeartbeat(timestamp);
            break;
        case DEVICE_EVENT:
            manageDeviceEventMessage(data);
            break;
        default:
            break;
    }
};

const proceedConnectionChange = (data) => {
    const {payload} = data || {};
    const {connectionStatus, serialNumber} = payload || {};
    const mappedData = uamfMappingService.connectionStatusFieldsMapping({...payload, status: connectionStatus});

    log.debug(
        `uamfWebSocketResponseService: [CONNECTION_STATUS_CHANGE] Device ${serialNumber}, status: ${mappedData.status}`
    );

    uamfDeviceService.applyDeviceConnectionStatus(serialNumber, mappedData);
};

const manageAsyncRequestMessage = (data) => {
    const requestData = data?.payload;
    const {requestId, status, request} = requestData || {};

    log.debug(
        `uamfWebSocketResponseService: [ASYNC_REQUEST] RequestId: ${requestId}, Status: ${status}, Action: ${request?.action}`
    );

    uamfAsyncRequestService.completeRequest(requestData);
};

const manageDeviceDataChangeMessage = (data) => {
    const {payload} = data || {};
    const {serialNumber, updatedData} = payload || {};

    if (!updatedData?.length) return;

    log.debug(
        `uamfWebSocketResponseService: [DEVICE_DATA_CHANGE] Device ${serialNumber}, updated fields: ${updatedData.length}`
    );

    const settingsUpdate = {};

    updatedData.forEach(({propertyName, newValue}) => {
        if (!propertyName || !newValue) return;

        const value = newValue.featureState ?? newValue.percentage;

        if (value !== undefined && value !== null) {
            settingsUpdate[propertyName] = value;
        }
    });

    if (Object.keys(settingsUpdate).length > 0) {
        dispatch(updateDeviceData(serialNumber, {settings: settingsUpdate}));
    }
};

const manageDeviceEventMessage = (data) => {
    const events = data?.payload?.events;

    if (!events || events.length === 0) {
        log.debug('uamfWebSocketResponseService: [DEVICE_EVENT] No events in payload');
        return;
    }

    const subEventType = events[0].eventType;

    switch (subEventType) {
        case HOLDER_INSERTED:
            handleHolderInserted(data);
            break;
        case HOLDER_EXTRACTED:
            handleHolderExtracted(data);
            break;
        default:
            log.debug(`uamfWebSocketResponseService: [DEVICE_EVENT] Unknown event type: ${subEventType}`);
            break;
    }
};

const handleHolderInserted = (data) => {
    const chargerDusn = data?.payload?.serialNumber;
    const events = data?.payload?.events;

    if (!events || events.length === 0) {
        log.debug('uamfWebSocketResponseService: [HOLDER_INSERTED] No events in payload');
        return;
    }

    const details = events[0].details;

    if (!details) {
        log.debug('uamfWebSocketResponseService: [HOLDER_INSERTED] No details in event');
        return;
    }

    const holderSerialNumber = details?.holderSerialNumber;

    if (!holderSerialNumber) {
        log.debug('uamfWebSocketResponseService: [HOLDER_INSERTED] Missing holderSerialNumber');
        return;
    }

    const isHolderRegistered = selectIsDeviceRegisteredByDusn(holderSerialNumber)(getState());

    if (!isHolderRegistered) {
        log.debug(`uamfWebSocketResponseService: [HOLDER_INSERTED] Holder not registered, dusn: ${holderSerialNumber}`);
        return;
    }

    log.debug(
        `uamfWebSocketResponseService: [HOLDER_INSERTED] Linking holder ${holderSerialNumber} to charger ${chargerDusn}`
    );
    dispatch(setHolderLastChargerDusn(holderSerialNumber, chargerDusn));
};

const handleHolderExtracted = (data) => {
    const chargerDusn = data?.payload?.serialNumber;
    const holderDusn = data?.payload?.events[0]?.details?.holderSerialNumber;

    log.debug(
        `uamfWebSocketResponseService: [HOLDER_EXTRACTED] Charger dusn: ${chargerDusn}, holder dusn: ${holderDusn}`
    );
};

const handleHeartbeat = (timestamp) => {
    log.debug(`uamfWebSocketResponseService: [HEARTBEAT] Received at ${timestamp}`);
};

export default {
    proceedMessage,
};
